import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../core/apiClient.ts';
import { FACEBOOK_POSITION_GROUPS, PLATFORM_LABELS } from '../../core/constants.ts';
import type { Country, LaunchTemplateConfig, Locale } from './types.ts';

type Props = { initialName?: string; initialConfig: LaunchTemplateConfig; onSave: (name: string, config: LaunchTemplateConfig) => Promise<void>; onCancel: () => void };
const CTA_TYPES = ['LEARN_MORE', 'SHOP_NOW', 'SIGN_UP', 'CONTACT_US', 'BOOK_TRAVEL', 'GET_OFFER'];
const MACROS = [['{{BUNDLE.NAME}}', 'Bundle name'], ['{{DOLPHIN.ACCOUNT.NAME}}', 'Dolphin account name'], ['{{CAB.ID}}', 'Ad account ID'], ['{{CAB.NAME}}', 'Ad account name'], ['{{PIXEL.ID}}', 'Pixel ID'], ['{{CAMPAIGN.ID}}', 'Campaign ID'], ['{{BM.NAME}}', 'Business Manager name'], ['{{CREATIVE.NAME}}', 'Creative file names'], ['{{DATE}}', 'Current date'], ['{{DATETIME}}', 'Current date and time'], ['{{RANDOM.DIGITS.6}}', 'Random digits'], ['{{RANDOM.LETTERS.EN.6}}', 'Random Latin letters'], ['{{RANDOM.LETTERS.RU.6}}', 'Random Cyrillic letters'], ['{{CAMPAIGN.NUMBER}}', 'Campaign sequence number'], ['{{AGE}}', 'Age'], ['{{GENDER}}', 'Gender'], ['{{PAGE.NAME}}', 'Page name'], ['{{PAGE.ID}}', 'Page ID'], ['{{APP.ID}}', 'App ID'], ['{{APP.NAME}}', 'App name'], ['{{CAB.TIME}}', 'Ad account timezone']] as const;
const toggle = <T,>(items: T[], item: T) => items.includes(item) ? items.filter((x) => x !== item) : [...items, item];

const ALIASES: Record<string, string> = {
  czechia: 'czech republic',
  uk: 'united kingdom',
  usa: 'united states',
  us: 'united states',
  ua: 'ukraine',
};

function matchTokenToItem(token: string, items: { id: string; label: string }[]) {
  let clean = token.trim().toLowerCase();
  if (!clean) return null;
  if (ALIASES[clean]) clean = ALIASES[clean];

  // 1. Exact ID match (e.g. "US", "DE")
  const byId = items.find((i) => i.id.toLowerCase() === clean);
  if (byId) return byId.id;

  // 2. Name match before parenthesis: "Germany (DE)" -> "germany"
  const byName = items.find((i) => {
    const nameOnly = i.label.replace(/\s*\([A-Z0-9+]+\)$/i, '').trim().toLowerCase();
    return nameOnly === clean;
  });
  if (byName) return byName.id;

  // 3. Partial match (name contains clean token or clean token contains name)
  const byPartial = items.find((i) => {
    const nameOnly = i.label.replace(/\s*\([A-Z0-9+]+\)$/i, '').trim().toLowerCase();
    return nameOnly.includes(clean) || clean.includes(nameOnly);
  });
  if (byPartial) return byPartial.id;

  return null;
}

export function TemplateForm({ initialName = '', initialConfig, onSave, onCancel }: Props) {
  const [name, setName] = useState(initialName);
  const [config, setConfig] = useState<LaunchTemplateConfig>(initialConfig);
  const [locales, setLocales] = useState<Locale[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [localeQuery, setLocaleQuery] = useState('');
  const [countryQuery, setCountryQuery] = useState('');
  const [loadingLocales, setLoadingLocales] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadLocales = async (resync = false) => {
    setLoadingLocales(true);
    try {
      setLocales(await (resync ? api.post<Locale[]>('/api/locales/resync') : api.get<Locale[]>('/api/locales')));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load locales');
    } finally {
      setLoadingLocales(false);
    }
  };

  const loadCountries = async (resync = false) => {
    try {
      setCountries(await (resync ? api.post<Country[]>('/api/countries/resync') : api.get<Country[]>('/api/countries')));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load countries');
    }
  };

  useEffect(() => { loadLocales(); loadCountries(); }, []);

  const selectedLocales = useMemo(() => locales.filter((item) => config.adset.locale_ids.includes(item.key)), [locales, config.adset.locale_ids]);
  const setAdset = (patch: Partial<LaunchTemplateConfig['adset']>) => setConfig((old) => ({ ...old, adset: { ...old.adset, ...patch } }));
  const setCampaign = (patch: Partial<LaunchTemplateConfig['campaign']>) => setConfig((old) => ({ ...old, campaign: { ...old.campaign, ...patch } }));
  const setAd = (patch: Partial<LaunchTemplateConfig['ad']>) => setConfig((old) => ({ ...old, ad: { ...old.ad, ...patch } }));

  const handleBatchAddCountries = (newIds: string[]) => {
    const combined = [...new Set([...config.adset.countries, ...newIds])];
    setAdset({ countries: combined });
  };

  const handleBatchAddLocales = (newIds: string[]) => {
    const combined = [...new Set([...config.adset.locale_ids, ...newIds.map(Number)])];
    setAdset({ locale_ids: combined });
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      await onSave(name, config);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="template-form glass-card" onSubmit={submit}>
      <div className="template-form__heading">
        <div>
          <h2>{initialName ? 'Edit template' : 'Create template'}</h2>
          <p>Save campaign, targeting, placements, and creative defaults.</p>
        </div>
        <button type="button" className="btn btn--ghost" onClick={onCancel}>Close</button>
      </div>
      {error && <div className="notification notification--error">{error}</div>}
      <section className="template-section">
        <h3>Campaign</h3>
        <div className="template-grid">
          <Field label="Template name"><input className="input" required value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <MacroInput label="Campaign name pattern" value={config.campaign.name_pattern} onChange={(value) => setCampaign({ name_pattern: value })} />
          <Field label="Objective"><select className="input" disabled value="OUTCOME_SALES"><option>OUTCOME_SALES</option></select></Field>
          <Field label="Budget">
            <div className="inline-fields">
              <input className="input" min="0.01" step="0.01" type="number" value={config.campaign.budget_amount} onChange={(e) => setCampaign({ budget_amount: Number(e.target.value) })} />
              <select className="input" value={config.campaign.budget_type} onChange={(e) => setCampaign({ budget_type: e.target.value as 'daily' | 'lifetime' })}>
                <option value="daily">Daily</option>
                <option value="lifetime">Lifetime</option>
              </select>
            </div>
          </Field>
          <Field label="Budget level"><Select value={config.campaign.budget_level} onChange={(value) => setCampaign({ budget_level: value as 'campaign' | 'adset' })} options={[['campaign', 'Campaign'], ['adset', 'Ad set']]} /></Field>
          <Field label="Bid strategy"><Select value={config.campaign.bid_strategy} onChange={(value) => setCampaign({ bid_strategy: value as any })} options={[['highest_volume', 'Highest volume'], ['bid_cap', 'Bid cap'], ['cost_cap', 'Cost cap']]} /></Field>
        </div>
      </section>

      <section className="template-section">
        <h3>Ad set</h3>
        <div className="template-grid">
          <MacroInput label="Ad set name pattern" value={config.adset.name_pattern} onChange={(value) => setAdset({ name_pattern: value })} />
          <Field label="Pixel event"><Select value={config.adset.pixel_event} onChange={(value) => setAdset({ pixel_event: value as any })} options={[['purchase', 'Purchase'], ['lead', 'Lead'], ['registration', 'Registration'], ['subscribe', 'Subscribe']]} /></Field>
          <Field label="Conversion window"><select className="input" disabled><option>7d click / 1d view</option></select></Field>
          <Field label="Advantage+ audience"><label className="toggle"><input type="checkbox" checked={config.adset.advantage_audience} onChange={(e) => setAdset({ advantage_audience: e.target.checked })} /> Enabled</label></Field>
          <Field label="Age range">
            <div className="inline-fields">
              <input className="input" type="number" min="13" value={config.adset.age_min} onChange={(e) => setAdset({ age_min: Number(e.target.value) })} />
              <input className="input" type="number" min="13" value={config.adset.age_max} onChange={(e) => setAdset({ age_max: Number(e.target.value) })} />
            </div>
          </Field>
          <Field label="Gender"><Select value={config.adset.gender} onChange={(value) => setAdset({ gender: value as any })} options={[['all', 'All'], ['male', 'Male'], ['female', 'Female']]} /></Field>
        </div>

        <SearchMultiSelect
          label="Countries"
          query={countryQuery}
          setQuery={setCountryQuery}
          items={countries.map((item) => ({ id: item.countryCode, label: `${item.name} (${item.countryCode})` }))}
          selected={config.adset.countries}
          onToggle={(id) => setAdset({ countries: toggle(config.adset.countries, id) })}
          onBatchAdd={handleBatchAddCountries}
          onRefresh={() => loadCountries(true)}
          placeholder="Search or paste comma-separated list (e.g. Germany, Italy, Poland)"
        />

        <SearchMultiSelect
          label="Locales"
          query={localeQuery}
          setQuery={setLocaleQuery}
          items={locales.map((item) => ({ id: String(item.key), label: item.name }))}
          selected={config.adset.locale_ids.map(String)}
          onToggle={(id) => setAdset({ locale_ids: toggle(config.adset.locale_ids, Number(id)) })}
          onBatchAdd={handleBatchAddLocales}
          onRefresh={() => loadLocales(true)}
          loading={loadingLocales}
        />
        {selectedLocales.length > 0 && <div className="text-xs text-muted">Selected: {selectedLocales.map((x) => x.name).join(', ')}</div>}

        <Field label="Placements">
          <div className="inline-fields">
            <label className="toggle"><input type="radio" checked={config.adset.placements_mode === 'automatic'} onChange={() => setAdset({ placements_mode: 'automatic', platforms: [], positions: [] })} /> Automatic</label>
            <label className="toggle"><input type="radio" checked={config.adset.placements_mode === 'manual'} onChange={() => setAdset({ placements_mode: 'manual' })} /> Manual</label>
          </div>
        </Field>
        {config.adset.placements_mode === 'manual' && (
          <div className="placement-box">
            <div className="checks">{Object.entries(PLATFORM_LABELS).map(([key, label]) => <Check key={key} label={label} checked={config.adset.platforms.includes(key)} onChange={() => { const selected = config.adset.platforms.includes(key); const facebookPositions = FACEBOOK_POSITION_GROUPS.flatMap((group) => group.positions.map(([position]) => position)) as string[]; setAdset({ platforms: toggle(config.adset.platforms, key), positions: key === 'facebook' ? (selected ? config.adset.positions.filter((position) => !facebookPositions.includes(position)) : [...new Set([...config.adset.positions, ...facebookPositions])]) : config.adset.positions }); }} />)}</div>
            {config.adset.platforms.includes('facebook') && <div className="position-tree">{FACEBOOK_POSITION_GROUPS.map((group) => <div key={group.label}><strong>{group.label}</strong><div className="checks">{group.positions.map(([key, label]) => <Check key={key} label={label} checked={config.adset.positions.includes(key)} onChange={() => setAdset({ positions: toggle(config.adset.positions, key) })} />)}</div></div>)}</div>}
            {config.adset.platforms.filter((x) => x !== 'facebook').map((x) => <p className="text-xs text-muted" key={x}>{PLATFORM_LABELS[x]} positions will be available soon.</p>)}
          </div>
        )}
        <Field label="Devices">
          <div className="checks">
            <Check label="Mobile" checked={config.adset.devices.includes('mobile')} onChange={() => setAdset({ devices: toggle(config.adset.devices, 'mobile') })} />
            <Check label="Desktop" checked={config.adset.devices.includes('desktop')} onChange={() => setAdset({ devices: toggle(config.adset.devices, 'desktop') })} />
          </div>
        </Field>
      </section>

      <section className="template-section">
        <h3>Ad</h3>
        <div className="template-grid">
          <MacroInput label="Ad name pattern" value={config.ad.name_pattern} onChange={(value) => setAd({ name_pattern: value })} />
          <Field label="Ad setup"><div className="inline-fields"><label className="toggle"><input type="radio" checked readOnly /> Use existing post</label><label className="toggle text-subtle"><input type="radio" disabled /> Create ad (Soon)</label></div></Field>
          <Field label="Call to action"><Select value={config.ad.cta_mode} onChange={(value) => setAd({ cta_mode: value as any, cta_type: value === 'none' ? null : config.ad.cta_type })} options={[['none', 'Do not create'], ['select_existing', 'Select existing']]} /></Field>
          {config.ad.cta_mode === 'select_existing' && <Field label="CTA type"><select className="input" value={config.ad.cta_type ?? ''} onChange={(e) => setAd({ cta_type: e.target.value || null })}><option value="">Select CTA</option>{CTA_TYPES.map((item) => <option key={item}>{item}</option>)}</select></Field>}
        </div>
        <Field label="Advantage+ creative enhancements">
          <div className="checks">
            {Object.entries(config.ad.advantage_plus_creative).map(([key, enabled]) => <Check key={key} label={key.replace(/_/g, ' ')} checked={enabled} onChange={() => setAd({ advantage_plus_creative: { ...config.ad.advantage_plus_creative, [key]: !enabled } })} />)}
          </div>
        </Field>
      </section>
      <div className="template-form__actions">
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Cancel</button>
        <button className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save template'}</button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) { return <label className="field"><span className="label">{label}</span>{children}</label>; }
function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) { return <label className="check"><input type="checkbox" checked={checked} onChange={onChange} /> {label}</label>; }
function Select({ value, onChange, options }: { value: string; onChange: (value: string) => void; options: readonly (readonly [string, string])[] }) { return <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>{options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>; }

function SearchMultiSelect({
  label,
  query,
  setQuery,
  items,
  selected,
  onToggle,
  onBatchAdd,
  onRefresh,
  loading,
  placeholder,
}: {
  label: string;
  query: string;
  setQuery: (value: string) => void;
  items: { id: string; label: string }[];
  selected: string[];
  onToggle: (id: string) => void;
  onBatchAdd?: (ids: string[]) => void;
  onRefresh: () => void;
  loading?: boolean;
  placeholder?: string;
}) {
  const parseAndAdd = (text: string): string => {
    if (!text.includes(',') && !text.includes(';') && !text.includes('\n')) return text;
    const tokens = text.split(/[\n,;]+/);
    const toParse = tokens.slice(0, -1);
    const remainder = tokens[tokens.length - 1] ?? '';

    const foundIds: string[] = [];
    for (const rawToken of toParse) {
      const matchedId = matchTokenToItem(rawToken, items);
      if (matchedId && !selected.includes(matchedId) && !foundIds.includes(matchedId)) {
        foundIds.push(matchedId);
      }
    }

    if (foundIds.length > 0) {
      if (onBatchAdd) {
        onBatchAdd(foundIds);
      } else {
        foundIds.forEach((id) => onToggle(id));
      }
    }
    return remainder;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.includes(',') || val.includes(';')) {
      const remainder = parseAndAdd(val);
      setQuery(remainder);
    } else {
      setQuery(val);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    if (pasted.includes(',') || pasted.includes(';') || pasted.includes('\n')) {
      e.preventDefault();
      const tokens = pasted.split(/[\n,;]+/);
      const foundIds: string[] = [];
      for (const rawToken of tokens) {
        const matchedId = matchTokenToItem(rawToken, items);
        if (matchedId && !selected.includes(matchedId) && !foundIds.includes(matchedId)) {
          foundIds.push(matchedId);
        }
      }
      if (foundIds.length > 0) {
        if (onBatchAdd) {
          onBatchAdd(foundIds);
        } else {
          foundIds.forEach((id) => onToggle(id));
        }
        setQuery('');
      } else {
        // No matches found (e.g. countries not loaded yet) — fall back to pasting as plain text
        setQuery(query + pasted);
      }
    }
  };

  const matches = items.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()) && !selected.includes(item.id)).slice(0, 12);

  return (
    <Field label={label}>
      <div className="multi-select">
        <div className="multi-select__control">
          {items.filter((item) => selected.includes(item.id)).map((item) => (
            <button type="button" className="multi-select__chip" key={item.id} onClick={() => onToggle(item.id)}>
              {item.label} ×
            </button>
          ))}
          <input
            className="input"
            placeholder={placeholder ?? `Search or paste ${label.toLowerCase()}`}
            value={query}
            onChange={handleInputChange}
            onPaste={handlePaste}
          />
        </div>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onRefresh} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>
      {query && (
        <div className="multi-select__results">
          {matches.map((item) => (
            <button
              type="button"
              key={item.id}
              onClick={() => {
                onToggle(item.id);
                setQuery('');
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </Field>
  );
}

function MacroInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Field label={label}>
      <div className="macro-input">
        <input className="input" value={value} onChange={(e) => onChange(e.target.value)} />
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setOpen(!open)}>Macros</button>
        {open && (
          <div className="macro-menu">
            {MACROS.map(([macro, description]) => (
              <button
                type="button"
                key={macro}
                onClick={() => {
                  onChange(value + macro);
                  setOpen(false);
                }}
              >
                <code>{macro}</code>
                <span>{description}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}
